import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { debounce } from "lodash";
import {
  listItems,
  getCategories,
  toggleArchive as rawToggleArchive,
} from "api/items";

import { CreateItemModal } from "components/items/CreateItemModal";
import { EditItemModal } from "components/items/EditItemModal";
import { IncomingModal } from "components/items/IncomingModal";
import { Button } from "components/ui/Button";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { BarcodeModal } from "components/items/BarcodeModal";
import { ProductDetailsModal } from "components/items/ProductDetailsModal";

/* ================== Вспомогательные и адаптер ================== */
const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmtMoney = (v, digits = 0) => {
  try {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: "UZS",
      maximumFractionDigits: digits,
      minimumFractionDigits: digits,
    }).format(v || 0);
  } catch {
    return `${Math.round(v || 0)} so'm`;
  }
};
const getStatus = (qty, min, isArchived) => {
  const q = safeNum(qty);
  const m = safeNum(min);
  if (isArchived) return { status: "archived" };
  if (q <= 0) return { status: "deficit" };
  if (q > 0 && q < m) return { status: "lowStock" };
  return { status: "inStock" };
};

const toggleArchive = (idOrObj, maybeArchived) => {
  if (typeof idOrObj === "object") return rawToggleArchive(idOrObj);
  return rawToggleArchive({ id: idOrObj, archived: !!maybeArchived });
};

/* ================== Иконки ================== */
const PlusIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
      clipRule="evenodd"
    />
  </svg>
);
const IncomingIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M3 3a1 1 0 000 2v10a1 1 0 001 1h12a1 1 0 001-1V5a1 1 0 000-2H3zm1 2a1 1 0 011-1h8a1 1 0 110 2H5a1 1 0 01-1-1z"
      clipRule="evenodd"
    />
  </svg>
);
const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
      clipRule="evenodd"
    />
  </svg>
);
const EditIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
    <path
      fillRule="evenodd"
      d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
      clipRule="evenodd"
    />
  </svg>
);
const BarcodeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
  </svg>
);
const ArchiveIcon = ({ archived }) =>
  archived ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.993.883L4 8v9a2 2 0 002 2h8a2 2 0 002-2V8a1 1 0 00-1-1h-1V6a4 4 0 00-4-4zm-2 5V6a2 2 0 114 0v1h-4z" />
    </svg>
  ) : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
        clipRule="evenodd"
      />
    </svg>
  );
const EyeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
    <path
      fillRule="evenodd"
      d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
      clipRule="evenodd"
    />
  </svg>
);
const GridIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);
const ListIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
      clipRule="evenodd"
    />
  </svg>
);

/* ================== Страница ================== */
export default function ItemsPage() {
  const [q, setQ] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("itemViewMode") || "grid"
  );

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  // ИЗМЕНЕНО: pageSize теперь в состоянии и сохраняется в localStorage
  const [pageSize, setPageSize] = useState(
    () => Number(localStorage.getItem("itemViewPageSize")) || 24
  );
  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  // Модалки
  const [openCreate, setOpenCreate] = useState(false);
  const [openIncoming, setOpenIncoming] = useState(false);
  const [incomingFor, setIncomingFor] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [viewItemId, setViewItemId] = useState(null);
  const [barcodeItem, setBarcodeItem] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const searchRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === "Escape" && document.activeElement === searchRef.current) {
        setQ("");
        searchRef.current?.blur();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Эффект для сохранения viewMode и сброса страницы
  useEffect(() => {
    try {
      localStorage.setItem("itemViewMode", viewMode);
    } catch { }
    setPage(1);
  }, [viewMode]);

  // НОВОЕ: Эффект для сохранения pageSize и сброса страницы
  useEffect(() => {
    try {
      localStorage.setItem("itemViewPageSize", String(pageSize));
    } catch { }
    setPage(1);
  }, [pageSize]);

  const fetchCats = async () => {
    try {
      setCats(await getCategories());
    } catch (e) {
      console.error(e);
    }
  };

  const fetchRows = useCallback(async (filters, currentPage, currentSize) => {
    setLoading(true);
    try {
      const offset = (currentPage - 1) * currentSize;
      const { rows, count } = await listItems({
        ...filters,
        limit: currentSize,
        offset,
      });
      setRows(Array.isArray(rows) ? rows : []);
      setCount(Number.isFinite(count) ? count : 0);
    } catch (e) {
      console.error("Failed to fetch items:", e?.message || e);
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedFetch = useCallback(
    debounce((...args) => fetchRows(...args), 300),
    [fetchRows]
  );
  useEffect(() => () => debouncedFetch.cancel?.(), [debouncedFetch]);

  useEffect(() => {
    fetchCats();
  }, []);

  // ИЗМЕНЕНО: pageSize добавлен в зависимости для сброса страницы при изменении фильтров
  useEffect(() => {
    const filters = { q, category_id: categoryId, show_archived: showArchived };
    if (page === 1) {
      debouncedFetch(filters, 1, pageSize);
    } else {
      setPage(1);
    }
  }, [q, categoryId, showArchived, debouncedFetch, pageSize]);

  // ИЗМЕНЕНО: pageSize и другие зависимости остались для перезагрузки при смене страницы
  useEffect(() => {
    const filters = { q, category_id: categoryId, show_archived: showArchived };
    fetchRows(filters, page, pageSize);
  }, [page, pageSize, q, categoryId, showArchived, fetchRows]);

  const refreshData = (updatedItem = null) => {
    const filters = { q, category_id: categoryId, show_archived: showArchived };
    fetchRows(filters, page, pageSize);
    // Если передан обновленный товар, обновляем editItem
    if (updatedItem && editItem && updatedItem.id === editItem.id) {
      setEditItem(updatedItem);
    }
  };

  const clearFilters = () => {
    setQ("");
    setCategoryId("");
    setShowArchived(false);
  };

  const onArchiveToggle = (row) => setConfirmRow(row);
  const doArchiveToggle = async () => {
    if (!confirmRow) return;
    setConfirmLoading(true);
    try {
      await toggleArchive({
        id: confirmRow.id,
        archived: !confirmRow.is_archived,
      });
      refreshData();
      setConfirmRow(null);
    } catch (e) {
      alert(e?.message || "Arxiv holatini o'zgartirib bo'lmadi");
    } finally {
      setConfirmLoading(false);
    }
  };

  const isFiltersActive = !!(q || categoryId || showArchived);

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50/50 dark:bg-slate-900 min-h-screen transition-colors">
      <div className="max-w-screen-xl mx-auto space-y-8">
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-100">
              Ombor
            </h1>
            <p className="mt-1 text-md text-gray-600 dark:text-slate-400">
              Mahsulotlar katalogi va qoldiqlar boshqaruvi.
            </p>
          </div>
          <div className="w-full sm:w-auto grid grid-cols-2 sm:flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setIncomingFor(null);
                setOpenIncoming(true);
              }}
              className="bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
            >
              <IncomingIcon />
              Kirim
            </Button>
            <Button
              onClick={() => setOpenCreate(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
            >
              <PlusIcon />
              Yangi mahsulot
            </Button>
          </div>
        </header>

        <div className="flex flex-col md:flex-row items-center gap-4 flex-wrap">
          <div className="relative w-full md:flex-1 md:min-w-[250px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-400">
                <SearchIcon />
              </span>
            </div>
            <input
              ref={searchRef}
              type="text"
              placeholder="Qidirish… (/ bosing)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-sm text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500"
            />
          </div>
          <select
            className="w-full md:w-auto bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-sm py-2 px-3 text-gray-900 dark:text-slate-100"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">Barcha toifalar</option>
            {cats.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <Switch value={showArchived} onChange={setShowArchived}>
            Arxivdagilarni ko'rsatish
          </Switch>
          <div className="hidden md:flex flex-grow" />
          {/* НОВОЕ: Селектор для выбора количества товаров на странице */}
          <PageSizeSelector value={pageSize} onChange={setPageSize} />
          <ViewSwitcher currentView={viewMode} onChange={setViewMode} />
          {isFiltersActive && (
            <Button variant="ghost" onClick={clearFilters}>
              Tozalash
            </Button>
          )}
        </div>

        <div>
          {loading ? (
            viewMode === "grid" ? (
              <SkeletonGrid pageSize={pageSize} />
            ) : (
              <SkeletonLayout pageSize={pageSize} />
            )
          ) : rows.length > 0 ? (
            viewMode === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {rows.map((row) => (
                  <ItemCard
                    key={row.id}
                    item={row}
                    onView={(it) => setViewItemId(it.id)}
                    onEdit={setEditItem}
                    onArchiveToggle={onArchiveToggle}
                    onIncoming={(it) => {
                      setOpenIncoming(true);
                      setIncomingFor(it);
                    }}
                    onBarcode={setBarcodeItem}
                  />
                ))}
              </div>
            ) : (
              <ItemsTable
                rows={rows}
                onEdit={setEditItem}
                onArchiveToggle={onArchiveToggle}
                onIncoming={(it) => {
                  setOpenIncoming(true);
                  setIncomingFor(it);
                }}
                onBarcode={setBarcodeItem}
                onView={(it) => setViewItemId(it.id)}
              />
            )
          ) : (
            <EmptyState onCreate={() => setOpenCreate(true)} />
          )}
          {count > pageSize && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalCount={count}
              pageSize={pageSize}
              onPageChange={(p) => {
                const next = Math.min(Math.max(1, p), totalPages);
                if (next !== page) setPage(next);
              }}
            />
          )}
        </div>
      </div>
      <CreateItemModal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={refreshData}
      />
      <IncomingModal
        open={openIncoming || !!incomingFor}
        onClose={() => {
          setOpenIncoming(false);
          setIncomingFor(null);
        }}
        onCreated={refreshData}
        fixedItemId={incomingFor?.id}
      />
      <EditItemModal
        open={!!editItem}
        onClose={() => setEditItem(null)}
        item={editItem}
        onUpdated={refreshData}
      />
      <BarcodeModal
        open={!!barcodeItem}
        onClose={() => setBarcodeItem(null)}
        item={barcodeItem}
      />
      <ProductDetailsModal
        open={!!viewItemId}
        itemId={viewItemId}
        onClose={() => setViewItemId(null)}
      />
      <ConfirmDialog
        open={!!confirmRow}
        onClose={() => setConfirmRow(null)}
        title={
          confirmRow?.is_archived ? "Arxivdan qaytarish" : "Mahsulotni arxivlash"
        }
        message={`Siz rostan ham "${confirmRow?.name}" mahsulotini ${confirmRow?.is_archived ? "arxivdan qaytarishni" : "arxivlashni"
          } xohlaysizmi?`}
        okText={confirmRow?.is_archived ? "Qaytarish" : "Arxivlash"}
        onOk={doArchiveToggle}
        loading={confirmLoading}
      />
    </div>
  );
}

/* ================== Подкомпоненты ================== */
const ItemCard = ({ item, onEdit, onArchiveToggle, onIncoming, onBarcode, onView }) => {
  const { qty_on_hand, min_stock, is_archived, last_price } = item;
  const qty = safeNum(qty_on_hand);
  const min = safeNum(min_stock);
  const { status } = getStatus(qty, min, is_archived);
  const totalValue = qty * safeNum(last_price);
  const supplier = item?.supplier_name || null;

  return (
    <div
      className={`group relative bg-white dark:bg-slate-800 rounded-xl border border-gray-200/75 dark:border-slate-700/75 shadow-sm transition-all duration-300 hover:shadow-lg ${is_archived ? "opacity-60" : ""
        }`}
    >
      <div className="p-5 flex flex-col h-full">
        <div>
          <p className="text-xs text-gray-500 truncate">
            {item.category_name || "Toifasiz"}
          </p>
          <h3
            className="font-semibold text-gray-800 dark:text-slate-200 text-lg truncate"
            title={item.name}
          >
            {item.name}
          </h3>
          {item.is_consumable && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800 mb-1">
              Sarflanuvchi
            </span>
          )}
          <p className="text-xs text-gray-400 font-mono">
            {item.sku || "SKU yo'q"}
          </p>
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-1 truncate">
            Yetkazib beruvchi: <span className="text-gray-700 dark:text-slate-300">{supplier || "—"}</span>
          </p>
        </div>
        <div className="mt-auto pt-4 space-y-3">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-3xl font-bold font-mono text-gray-800 dark:text-slate-100">
                {qty}
              </p>
              <p className="text-sm text-gray-500 dark:text-slate-400">{item.unit || "dona"}</p>
            </div>
            <StatusIndicator status={status} />
          </div>
          <div className="text-right border-t border-gray-100 dark:border-slate-700 pt-2">
            <p className="text-sm font-semibold text-gray-700 dark:text-slate-300">
              {fmtMoney(totalValue, 0)}
            </p>
            <p className="text-xs text-gray-400">umumiy qiymat</p>
          </div>
        </div>
      </div>
      <div className="absolute top-2 right-2">
        <ActionsMenu
          item={item}
          onView={onView}
          onEdit={onEdit}
          onIncoming={onIncoming}
          onBarcode={onBarcode}
          onArchiveToggle={onArchiveToggle}
        />
      </div>
    </div>
  );
};

const ItemsTable = ({
  rows,
  onEdit,
  onArchiveToggle,
  onIncoming,
  onBarcode,
  onView,
}) => (
  <>
    {/* Мобильный вид */}
    <div className="sm:hidden space-y-4">
      {rows.map((item) => {
        const qty = safeNum(item.qty_on_hand);
        const min = safeNum(item.min_stock);
        const { status } = getStatus(qty, min, item.is_archived);
        const totalValue = qty * safeNum(item.last_price);
        const supplier = item?.supplier_name || null;

        return (
          <div
            key={item.id}
            className={`bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-4 space-y-3 ${item.is_archived ? "opacity-60" : ""
              }`}
          >
            <div className="flex justify-between items-start">
              <div className="min-w-0">
                <p className="font-semibold text-gray-800 dark:text-slate-200 truncate flex items-center gap-2">
                  {item.name}
                  {item.is_consumable && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800">
                      Sarflanuvchi
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-400 font-mono">
                  {item.sku || "SKU yo'q"}
                </p>
              </div>
              <ActionsMenu
                item={item}
                onView={onView}
                onEdit={onEdit}
                onIncoming={onIncoming}
                onBarcode={onBarcode}
                onArchiveToggle={onArchiveToggle}
              />
            </div>
            <div className="border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
              <Row label="Toifa" value={item.category_name || "—"} />
              <Row label="Yetkazib beruvchi" value={supplier || "—"} />
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500">Holat:</span>
                <StatusIndicator status={status} />
              </div>
              <div className="flex justify-between items-baseline pt-2">
                <span className="text-gray-500 dark:text-slate-400 text-sm">Qoldiq:</span>
                <p>
                  <span className="text-2xl font-bold font-mono text-gray-800 dark:text-slate-100">
                    {qty}
                  </span>{" "}
                  <span className="text-sm text-gray-500 dark:text-slate-500">
                    {item.unit || "dona"}
                  </span>
                </p>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-gray-500 dark:text-slate-400 text-sm">Jami:</span>
                <p className="font-semibold text-gray-700 dark:text-slate-300">
                  {fmtMoney(totalValue, 0)}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>

    {/* Десктопный вид (таблица) */}
    <div className="hidden sm:block overflow-x-auto rounded-lg border border-gray-200/75 dark:border-slate-700/75 bg-white dark:bg-slate-800">
      <table className="w-full">
        <thead className="bg-gray-50/75 dark:bg-slate-700/50 border-b border-gray-200/75 dark:border-slate-700/75">
          <tr className="text-left text-xs uppercase text-gray-500 dark:text-slate-400 tracking-wider">
            <Th className="pl-6 w-[28%]">Nomi</Th>
            <Th className="w-[15%]">Toifa</Th>
            <Th className="w-[18%]">Yetkazib beruvchi</Th>
            <Th className="text-right w-[10%]">Qoldiq</Th>
            <Th className="text-right w-[12%]">Birlik narxi</Th>
            <Th className="text-right w-[12%]">Jami</Th>
            <Th className="text-center w-[5%]">Holat</Th>
            <Th className="text-center pr-6 w-[10%]">Amallar</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
          {rows.map((item) => {
            const qty = safeNum(item.qty_on_hand);
            const min = safeNum(item.min_stock);
            const { status } = getStatus(qty, min, item.is_archived);
            const totalValue = qty * safeNum(item.last_price);
            const supplier = item?.supplier_name || null;

            return (
              <tr
                key={item.id}
                className={`group relative bg-white dark:bg-slate-800 transition-shadow hover:shadow-md ${item.is_archived ? "opacity-60" : ""
                  }`}
              >
                <Td className="pl-6">
                  <div className="font-semibold text-gray-800 dark:text-slate-200 flex items-center gap-2">
                    {item.name}
                    {item.is_consumable && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800">
                        Sarflanuvchi
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 font-mono mt-1">
                    {item.sku || "SKU yo'q"}
                  </div>
                </Td>
                <Td className="text-gray-600 dark:text-slate-400">{item.category_name || "—"}</Td>
                <Td className="text-gray-700 dark:text-slate-300">{supplier || "—"}</Td>
                <Td className="text-right">
                  <span className="font-mono font-bold text-lg text-gray-800 dark:text-slate-200">
                    {qty}
                  </span>
                  <span className="ml-1.5 text-xs text-gray-500 dark:text-slate-500">
                    {item.unit || "dona"}
                  </span>
                </Td>
                <Td className="text-right font-medium text-gray-600 dark:text-slate-400">
                  {fmtMoney(safeNum(item.last_price), 0)}
                </Td>
                <Td className="text-right font-bold text-gray-800 dark:text-slate-200">
                  {fmtMoney(totalValue, 0)}
                </Td>
                <Td className="text-center">
                  <StatusIndicator status={status} short />
                </Td>
                <Td className="pr-6 text-center">
                  <ActionsMenu
                    item={item}
                    onView={onView}
                    onEdit={onEdit}
                    onIncoming={onIncoming}
                    onBarcode={onBarcode}
                    onArchiveToggle={onArchiveToggle}
                  />
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </>
);

const Row = ({ label, value, mono = false }) => (
  <div className="flex justify-between items-center text-sm">
    <span className="text-gray-500 dark:text-slate-400">{label}:</span>
    <span className={`text-gray-700 dark:text-slate-300 ${mono ? "font-mono" : ""}`}>
      {value || "—"}
    </span>
  </div>
);

const ActionsMenu = ({
  item,
  onView,
  onEdit,
  onIncoming,
  onBarcode,
  onArchiveToggle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const recalc = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = 208; // w-52

    // Calculate left: right align to button
    let left = rect.right - menuWidth;

    // Viewport bound checking
    if (left < 8) left = 8;
    if (left + menuWidth > window.innerWidth - 8) {
      left = window.innerWidth - menuWidth - 8;
    }

    // Fixed position: viewport relative (no scrollY)
    setPosition({ top: rect.bottom + 8, left });
  }, []);

  const handleToggle = () => {
    if (!isOpen) recalc();
    setIsOpen((v) => !v);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    const onEvent = () => {
      if (isOpen) recalc();
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", onKey);
      window.addEventListener("scroll", onEvent, { passive: true });
      window.addEventListener("resize", onEvent);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onEvent);
      window.removeEventListener("resize", onEvent);
    };
  }, [isOpen, recalc]);

  const handleActionClick = (action) => {
    setIsOpen(false);
    setTimeout(() => action(item), 0);
  };

  return (
    <>
      <button
        ref={triggerRef}
        onClick={handleToggle}
        className="p-2 rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
        title="Amallar"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={{ top: `${position.top}px`, left: `${position.left}px` }}
            className="fixed w-52 bg-white rounded-lg shadow-xl ring-1 ring-black/5 z-50 p-2"
          >
            <MenuItem
              onClick={() => handleActionClick(onView)}
              icon={<EyeIcon />}
            >
              Подробнее
            </MenuItem>
            <MenuItem
              onClick={() => handleActionClick(onEdit)}
              icon={<EditIcon />}
            >
              Редактировать
            </MenuItem>
            {!item.is_archived && (
              <MenuItem
                onClick={() => handleActionClick(onIncoming)}
                icon={<IncomingIcon />}
              >
                Kirim
              </MenuItem>
            )}
            <MenuItem
              onClick={() => handleActionClick(onBarcode)}
              icon={<BarcodeIcon />}
            >
              Shtrix-kod
            </MenuItem>
            <div className="my-1 border-t border-gray-100" />
            <MenuItem
              onClick={() => handleActionClick(onArchiveToggle)}
              icon={<ArchiveIcon archived={item.is_archived} />}
              isDestructive={!item.is_archived}
            >
              {item.is_archived ? "Arxivdan qaytarish" : "Arxivlash"}
            </MenuItem>
          </div>,
          document.body
        )}
    </>
  );
};

const MenuItem = ({ children, icon, onClick, isDestructive = false }) => (
  <button
    onClick={onClick}
    className={`w-full text-left flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${isDestructive
      ? "text-red-600 hover:bg-red-50"
      : "text-gray-700 hover:bg-gray-100"
      }`}
  >
    <span className="opacity-70">{icon}</span>
    <span>{children}</span>
  </button>
);

const StatusIndicator = ({ status, short = false }) => {
  const statusConfig = {
    inStock: { text: "Mavjud", color: "bg-green-500" },
    lowStock: { text: "Kam qolgan", color: "bg-yellow-500" },
    deficit: { text: "Tugagan", color: "bg-red-500" },
    archived: { text: "Arxivda", color: "bg-gray-400" },
  };
  const { text, color } = statusConfig[status] || statusConfig.archived;
  return (
    <div className="flex items-center gap-2" title={text}>
      <div className={`h-2.5 w-2.5 rounded-full ${color}`} />
      {!short && <span className="text-sm text-gray-500">{text}</span>}
    </div>
  );
};
function Switch({ children, value, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <div className="relative">
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
        />
        <div
          className={`block w-10 h-6 rounded-full transition ${value ? "bg-blue-600 dark:bg-blue-500" : "bg-gray-300 dark:bg-slate-600"
            }`}
        ></div>
        <div
          className={`dot absolute left-1 top-1 bg-white dark:bg-slate-100 w-4 h-4 rounded-full transition transform ${value ? "translate-x-4" : ""
            }`}
        ></div>
      </div>
      <span className="text-sm text-gray-700 dark:text-slate-300">{children}</span>
    </label>
  );
}

function ViewSwitcher({ currentView, onChange }) {
  return (
    <div className="flex items-center p-1 bg-gray-200 dark:bg-slate-700 rounded-lg">
      <button
        onClick={() => onChange("grid")}
        className={`p-1.5 rounded-md transition-colors ${currentView === "grid"
          ? "bg-white dark:bg-slate-600 shadow-sm text-gray-900 dark:text-slate-100"
          : "text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200"
          }`}
        title="Plitka"
      >
        <GridIcon />
      </button>
      <button
        onClick={() => onChange("table")}
        className={`p-1.5 rounded-md transition-colors ${currentView === "table"
          ? "bg-white dark:bg-slate-600 shadow-sm text-gray-900 dark:text-slate-100"
          : "text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200"
          }`}
        title="Jadval"
      >
        <ListIcon />
      </button>
    </div>
  );
}

// НОВОЕ: Компонент для выбора количества элементов на странице
function PageSizeSelector({ value, onChange }) {
  const options = [12, 24, 48, 96];
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600 dark:text-slate-400 hidden lg:block">
        Sahifada:
      </span>
      <select
        className="w-full md:w-auto bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-sm py-2 px-3 text-sm text-gray-900 dark:text-slate-100"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {options.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>
    </div>
  );
}

function Pagination({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
}) {
  const from = (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalCount);
  return (
    <div className="mt-8 flex flex-col sm:flex-row items-center justify-between text-sm gap-4">
      <p className="text-gray-600 dark:text-slate-400 text-center sm:text-left">
        <span className="font-medium text-gray-900 dark:text-slate-200">{from}</span>-
        <span className="font-medium text-gray-900 dark:text-slate-200">{to}</span> dan <span className="font-medium text-gray-900 dark:text-slate-200">{totalCount}</span>
        {" "}ta ko'rsatilmoqda
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage <= 1}
          className="bg-white dark:bg-slate-800 dark:text-slate-100 dark:border-slate-700 disabled:text-gray-300 dark:disabled:text-slate-600"
          title="Orqaga"
        >
          <svg
            className="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </Button>
        <span className="text-gray-600 dark:text-slate-400 px-2">
          {currentPage} / {totalPages}
        </span>
        <Button
          variant="outline"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage >= totalPages}
          className="bg-white dark:bg-slate-800 dark:text-slate-100 dark:border-slate-700 disabled:text-gray-300 dark:disabled:text-slate-600"
          title="Oldinga"
        >
          <svg
            className="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </Button>
      </div>
    </div>
  );
}

const EmptyState = ({ onCreate }) => (
  <div className="py-20 text-center bg-white dark:bg-slate-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-slate-700 transition-colors hover:border-blue-500 dark:hover:border-blue-500">
    <svg
      className="mx-auto h-12 w-12 text-gray-400 dark:text-slate-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
      />
    </svg>
    <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-slate-100">
      Mahsulotlar ro'yxati bo'sh
    </h3>
    <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
      Tizimga birinchi mahsulotni qo'shishdan boshlang.
    </p>
    <div className="mt-6">
      <Button
        onClick={onCreate}
        className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
      >
        <PlusIcon /> Mahsulot qo'shish
      </Button>
    </div>
  </div>
);

const SkeletonGrid = ({ pageSize }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-pulse">
    {[...Array(pageSize)].map((_, i) => (
      <div
        key={i}
        className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200/75 dark:border-slate-700 shadow-sm p-5 flex flex-col h-52"
      >
        <div>
          <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-1/3 mb-2" />
          <div className="h-5 bg-gray-200 dark:bg-slate-700 rounded w-3/4" />
          <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-1/2 mt-1" />
        </div>
        <div className="mt-auto space-y-3">
          <div className="flex items-end justify-between">
            <div>
              <div className="h-8 bg-gray-300 dark:bg-slate-600 rounded w-16" />
            </div>
            <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded-full w-24" />
          </div>
          <div className="border-t border-gray-100 dark:border-slate-700 pt-2 flex justify-end">
            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/2" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const SkeletonLayout = ({ pageSize }) => (
  <div className="animate-pulse">
    <div className="hidden sm:block bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
      <div className="h-14 bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700" />
      <div className="divide-y divide-gray-100 dark:divide-slate-700 p-6 space-y-5">
        {[...Array(Math.floor(pageSize / 2))].map((_, i) => (
          <div key={i} className="flex items-center space-x-6 pt-5 first:pt-0">
            <div className="w-[28%] space-y-2">
              <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4" />
              <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-1/2" />
            </div>
            <div className="w-[15%] h-4 bg-gray-200 dark:bg-slate-700 rounded" />
            <div className="w-[18%] h-4 bg-gray-200 dark:bg-slate-700 rounded" />
            <div className="w-[10%] h-6 bg-gray-300 dark:bg-slate-600 rounded ml-auto" />
            <div className="w-[12%] h-4 bg-gray-200 dark:bg-slate-700 rounded ml-auto" />
            <div className="w-[12%] h-4 bg-gray-200 dark:bg-slate-700 rounded ml-auto" />
            <div className="w-[5%] flex justify-center">
              <div className="h-3 w-3 bg-gray-300 dark:bg-slate-600 rounded-full" />
            </div>
            <div className="w-[10%] flex justify-center">
              <div className="h-8 w-8 bg-gray-200 dark:bg-slate-700 rounded-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Th = ({ children, className = "" }) => (
  <th className={`py-3 px-6 font-semibold text-sm text-gray-500 dark:text-slate-400 ${className}`}>{children}</th>
);

const Td = ({ children, className = "" }) => (
  <td className={`py-4 px-6 text-gray-900 dark:text-slate-100 ${className}`}>{children}</td>
);
